import type { BusinessTypeValue } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const BOARD_ROUTES = { BOARD: '/saas/board' } as const;

const ENDPOINT = '/api/v1/saas/board';
const KEY = ['saas', 'board'];

export type BoardKind = 'ONBOARDING' | 'BILLING';
/** Passos da jornada de cadastro (spec 09), na ordem; DONE = jornada concluída. */
export type BoardSignupStep =
  | 'LEAD'
  | 'WHATSAPP_VERIFIED'
  | 'ACCOUNT'
  | 'COMPANY_DATA'
  | 'SERVICES'
  | 'PROFESSIONALS'
  | 'LANDING_PAGE'
  | 'WHATSAPP_CONNECTED'
  | 'CAMPAIGN_STARTED'
  | 'DONE';

export interface BoardCard {
  id: string;
  companyId: string;
  companyName: string;
  planName: string;
  column: string;
  detail: string;
  amount: number | null;
  late: boolean;
  onWaitlist: boolean;
  signupStatus: string;
}

export interface BoardData {
  columns: string[];
  data: BoardCard[];
}

/** Abrir o quadro e o que gera os cartoes: a busca roda a cada visita. */
export function useBoard(
  board: BoardKind,
  planId?: string,
  signupStep?: BoardSignupStep,
  profileType?: 'ESTABELECIMENTO' | 'AUTONOMO',
  businessType?: BusinessTypeValue,
  name?: string,
  createdFrom?: string,
  createdTo?: string,
) {
  const query = new URLSearchParams();
  if (planId) query.set('planId', planId);
  if (signupStep) query.set('signupStep', signupStep);
  if (profileType) query.set('profileType', profileType);
  if (businessType) query.set('businessType', businessType);
  if (name) query.set('name', name);
  if (createdFrom) query.set('createdFrom', createdFrom);
  if (createdTo) query.set('createdTo', createdTo);
  const queryString = query.toString();

  return useQuery({
    queryKey: [
      ...KEY,
      board,
      planId ?? 'ALL',
      signupStep ?? 'ALL',
      profileType ?? 'ALL',
      businessType ?? 'ALL',
      name ?? '',
      createdFrom ?? '',
      createdTo ?? '',
    ],
    queryFn: () =>
      apiClient.get<BoardData>(`${ENDPOINT}/${board}${queryString ? `?${queryString}` : ''}`),
  });
}

export function useMoveCard(
  board: BoardKind,
  planId?: string,
  signupStep?: BoardSignupStep,
  profileType?: 'ESTABELECIMENTO' | 'AUTONOMO',
  businessType?: BusinessTypeValue,
  name?: string,
  createdFrom?: string,
  createdTo?: string,
) {
  const queryClient = useQueryClient();
  const query = new URLSearchParams();
  if (planId) query.set('planId', planId);
  if (signupStep) query.set('signupStep', signupStep);
  if (profileType) query.set('profileType', profileType);
  if (businessType) query.set('businessType', businessType);
  if (name) query.set('name', name);
  if (createdFrom) query.set('createdFrom', createdFrom);
  if (createdTo) query.set('createdTo', createdTo);
  const queryString = query.toString();

  return useMutation({
    mutationFn: ({ id, column }: { id: string; column: string }) =>
      apiClient.patch<BoardData>(`${ENDPOINT}/cards/${id}${queryString ? `?${queryString}` : ''}`, {
        column,
      }),
    onSuccess: (dados) =>
      queryClient.setQueryData(
        [
          ...KEY,
          board,
          planId ?? 'ALL',
          signupStep ?? 'ALL',
          profileType ?? 'ALL',
          businessType ?? 'ALL',
          name ?? '',
          createdFrom ?? '',
          createdTo ?? '',
        ],
        dados,
      ),
  });
}

export interface SignupLogEntry {
  step: string;
  startedAt: string;
  completedAt: string | null;
}

export interface SignupLogResponse {
  companyId: string;
  companyName: string;
  signupStatus: string;
  data: SignupLogEntry[];
}

export function useSignupLog(cardId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'signup-log', cardId],
    enabled: Boolean(cardId),
    queryFn: () => apiClient.get<SignupLogResponse>(`${ENDPOINT}/cards/${cardId}/signup-log`),
  });
}
